import { StyleSheet, css } from 'aphrodite';
import { type FC, type ReactNode, useEffect, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';
import { RadioButton } from '../../../web/components/RadioButton';
import { LIBRARY_PRESETS, PIN_COLORS } from '../pins';
import type { KeywordPin, LibraryPreset, PinTrigger } from '../pins';
import { IconBook, IconCheck, IconClock, IconPinFill, IconX, IconZap } from './icons';

/**
 * Modal editor for creating or editing a keyword pin.
 *
 * When `initial` is `null`, the editor is in create mode and surfaces the
 * library-preset picker at the top. Picking a preset pre-fills every field
 * (except trigger) and dismisses the picker. Users can also hit "Skip" to
 * close the picker and build a pin from scratch.
 *
 * When `initial` is a `KeywordPin`, the editor is in edit mode: the picker
 * is hidden entirely and all fields start populated. `enabled` and `builtin`
 * are preserved from `initial`; the caller receives them unchanged in the
 * save payload so built-in pins stay built-in after editing.
 *
 * Close behavior: overlay click, ESC key, and the close X all fire
 * `onCancel`. Save is disabled until `label`, `keyword`, and `prompt` each
 * have non-whitespace content.
 *
 * Visual spec mirrors `/tmp/design-bundle/focused-view/project/PinEditor.jsx`.
 * We implement the overlay + card ourselves rather than wrapping
 * `components-v2/Dialog` because Dialog pulls in app-level colors and
 * shadows and forces a standard close button layout — the prototype's
 * color-tinted header icon, custom footer with left-aligned preview text,
 * and scoped `[data-focused-view]` CSS variables do not match that shell.
 */
type PinEditorProps = {
	initial: KeywordPin | null;
	onCancel: () => void;
	onSave: (patch: {
		id?: string;
		label: string;
		keyword: string;
		color: string;
		prompt: string;
		trigger: PinTrigger;
		enabled: boolean;
		builtin?: boolean;
	}) => void;
};

export const PinEditor: FC<PinEditorProps> = ({ initial, onCancel, onSave }) => {
	const [label, setLabel] = useState<string>(initial?.label ?? '');
	const [keyword, setKeyword] = useState<string>(initial?.keyword ?? '');
	const [color, setColor] = useState<string>(initial?.color ?? PIN_COLORS[0].value);
	const [prompt, setPrompt] = useState<string>(initial?.prompt ?? '');
	const [trigger, setTrigger] = useState<PinTrigger>(initial?.trigger ?? 'before_call');
	const [showLibrary, setShowLibrary] = useState<boolean>(!initial);

	const canSave = label.trim().length > 0 && keyword.trim().length > 0 && prompt.trim().length > 0;

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				onCancel();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onCancel]);

	const applyPreset = (preset: LibraryPreset) => {
		setLabel(preset.label);
		setKeyword(preset.keyword);
		setColor(preset.color);
		setPrompt(preset.prompt);
		setShowLibrary(false);
	};

	const handleSave = () => {
		if (!canSave) return;
		onSave({
			id: initial?.id,
			label: label.trim(),
			keyword: keyword.trim(),
			color,
			prompt: prompt.trim(),
			trigger,
			enabled: initial?.enabled ?? true,
			builtin: initial?.builtin ?? false,
		});
	};

	return (
		<div
			role='dialog'
			aria-modal='true'
			aria-label={initial ? 'Edit keyword pin' : 'New keyword pin'}
			onClick={onCancel}
			className={css(styles.overlay)}
		>
			<div onClick={e => e.stopPropagation()} className={css(styles.card)}>
				<div className={css(styles.header)}>
					<div className={css(styles.headerLeft)}>
						<span className={css(styles.headerIcon)} style={{ background: color }}>
							<IconPinFill size={14} color='#fff' />
						</span>
						<div>
							<div className={css(styles.headerTitle)}>{initial ? 'Edit keyword pin' : 'New keyword pin'}</div>
							<div className={css(styles.headerSub)}>Auto-pin any message starting with your keyword</div>
						</div>
					</div>
					<Button variant='ghost' size='icon' onClick={onCancel} aria-label='Close'>
						<IconX />
					</Button>
				</div>

				<div className={css(styles.body)}>
					{showLibrary && (
						<div className={css(styles.librarySection)}>
							<div className={css(styles.libraryHead)}>
								<IconBook size={13} />
								<span className={css(styles.colTitle)}>Start from library</span>
								<div className={css(styles.skipWrap)}>
									<Button variant='ghost' size='sm' onClick={() => setShowLibrary(false)}>
										Skip
									</Button>
								</div>
							</div>
							<div className={css(styles.presetGrid)}>
								{LIBRARY_PRESETS.map(p => (
									<Button
										key={p.label}
										variant='outline'
										size='sm'
										onClick={() => applyPreset(p)}
										styles={[styles.presetBtn]}
										style={{ borderLeftColor: p.color, borderLeftWidth: 3 }}
									>
										<span className={css(styles.presetBtnText)}>
											<span className={css(styles.presetLabel)}>{p.label}</span>
											<span className={css(styles.presetKeyword)}>&quot;{p.keyword}&quot;</span>
										</span>
									</Button>
								))}
							</div>
						</div>
					)}

					<div className={css(styles.fieldGrid)}>
						<div className={css(styles.field)}>
							<label className={css(styles.fieldLabel)}>Label</label>
							<input
								type='text'
								className={css(styles.input)}
								value={label}
								onChange={e => setLabel(e.target.value)}
								placeholder='e.g. Game Plan'
							/>
						</div>
						<div className={css(styles.field)}>
							<label className={css(styles.fieldLabel)}>Keyword trigger</label>
							<input
								type='text'
								className={css(styles.input, styles.inputMono)}
								value={keyword}
								onChange={e => setKeyword(e.target.value)}
								placeholder='game plan'
							/>
						</div>
					</div>

					<div className={css(styles.field)}>
						<label className={css(styles.fieldLabel)}>Border color</label>
						<div className={css(styles.swatchRow)}>
							{PIN_COLORS.map(c => {
								const active = color === c.value;
								return (
									<button
										key={c.value}
										type='button'
										title={c.name}
										aria-label={`${c.name} border`}
										aria-pressed={active}
										onClick={() => setColor(c.value)}
										className={css(styles.swatch, active && styles.swatchActive)}
										style={{ background: c.value }}
									/>
								);
							})}
						</div>
					</div>

					<div className={css(styles.field)}>
						<label className={css(styles.fieldLabel)}>Prompt</label>
						<textarea
							className={css(styles.textarea)}
							value={prompt}
							onChange={e => setPrompt(e.target.value)}
							placeholder='What should the AI respond with when this keyword is used?'
							rows={5}
						/>
					</div>

					<div className={css(styles.field)}>
						<label className={css(styles.fieldLabel)}>When to auto-send</label>
						<div className={css(styles.triggerGrid)}>
							<TriggerOption
								value='before_call'
								current={trigger}
								onSelect={setTrigger}
								title='Before call'
								sub='Runs when lead opens'
								icon={<IconClock size={14} />}
							/>
							<TriggerOption
								value='on_dial'
								current={trigger}
								onSelect={setTrigger}
								title='On dial'
								sub='Runs when dialed'
								icon={<IconZap size={14} />}
							/>
							<TriggerOption
								value='starter'
								current={trigger}
								onSelect={setTrigger}
								title='Conversation starter'
								sub='Adds to quick send list'
								icon={<IconBook size={14} />}
							/>
						</div>
					</div>
				</div>

				<div className={css(styles.footer)}>
					<span className={css(styles.footerHint)}>
						Messages starting with <span className={css(styles.footerKeyword)}>&quot;{keyword || 'keyword'}&quot;</span>{' '}
						will be pinned automatically.
					</span>
					<div className={css(styles.footerActions)}>
						<Button variant='outline' onClick={onCancel}>
							Cancel
						</Button>
						<Button variant='primary' onClick={handleSave} disabled={!canSave}>
							<IconCheck size={13} sw={2} />
							{initial ? 'Save changes' : 'Create pin'}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

type TriggerOptionProps = {
	value: PinTrigger;
	current: PinTrigger;
	onSelect: (value: PinTrigger) => void;
	title: string;
	sub: string;
	icon: ReactNode;
};

const TriggerOption: FC<TriggerOptionProps> = ({ value, current, onSelect, title, sub, icon }) => {
	const active = current === value;
	const id = `trigger-option-${value}`;
	return (
		<div
			onClick={() => onSelect(value)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSelect(value);
				}
			}}
			role='presentation'
			className={css(styles.triggerBtn, active && styles.triggerBtnActive)}
		>
			<div className={css(styles.triggerHead)}>
				<RadioButton id={id} name='pin-trigger' checked={active} onChange={() => onSelect(value)} value={value} />
				<div className={css(styles.triggerTitleRow)} style={{ color: active ? 'var(--ink)' : 'var(--ink-muted)' }}>
					{icon}
					<label htmlFor={id} className={css(styles.triggerTitle)}>
						{title}
					</label>
				</div>
			</div>
			<span className={css(styles.triggerSub)}>{sub}</span>
		</div>
	);
};

const styles = StyleSheet.create({
	overlay: {
		position: 'fixed',
		inset: 0,
		background: 'rgba(0,0,0,0.45)',
		backdropFilter: 'blur(4px)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 100,
	},
	card: {
		width: 520,
		maxWidth: 'calc(100vw - 40px)',
		maxHeight: 'calc(100vh - 40px)',
		background: 'var(--bg-elev)',
		borderRadius: 14,
		border: '1px solid var(--border)',
		boxShadow: '0 24px 48px -12px rgba(0,0,0,0.3)',
		display: 'flex',
		flexDirection: 'column',
	},
	header: {
		padding: '16px 20px',
		borderBottom: '1px solid var(--border)',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	headerIcon: {
		width: 28,
		height: 28,
		borderRadius: 8,
		color: '#fff',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: 'background 160ms',
	},
	headerTitle: {
		fontSize: 14,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	headerSub: {
		fontSize: 11.5,
		color: 'var(--ink-muted)',
	},
	body: {
		padding: 20,
		overflowY: 'auto',
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
	},
	librarySection: {
		background: 'var(--bg-sunken)',
		border: '1px solid var(--border)',
		borderRadius: 10,
		padding: 12,
	},
	libraryHead: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		marginBottom: 10,
		color: 'var(--ink-muted)',
	},
	colTitle: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	skipWrap: {
		marginLeft: 'auto',
	},
	presetGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 6,
	},
	presetBtn: {
		justifyContent: 'flex-start',
		height: 'auto',
		padding: '8px 10px',
		fontWeight: 500,
		textAlign: 'left',
	},
	presetBtnText: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 2,
	},
	presetLabel: {
		fontSize: 12,
		color: 'var(--ink)',
	},
	presetKeyword: {
		fontSize: 10.5,
		color: 'var(--ink-muted)',
		fontFamily: 'var(--font-mono)',
		fontWeight: 400,
	},
	fieldGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 12,
	},
	field: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	fieldLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
		color: 'var(--ink-muted)',
	},
	input: {
		height: 34,
		padding: '0 10px',
		border: '1px solid var(--border)',
		borderRadius: 8,
		background: 'var(--bg-elev)',
		color: 'var(--ink)',
		fontSize: 13,
		fontFamily: 'var(--font-sans)',
		outline: 'none',
		transition: 'border-color 120ms, box-shadow 120ms',
		':focus': {
			borderColor: 'var(--ink-faint)',
			boxShadow: '0 0 0 3px var(--ring)',
		},
	},
	inputMono: {
		fontFamily: 'var(--font-mono)',
	},
	swatchRow: {
		display: 'flex',
		gap: 8,
		flexWrap: 'wrap',
	},
	swatch: {
		width: 30,
		height: 30,
		borderRadius: 8,
		border: '2px solid transparent',
		boxShadow: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'transform 120ms, box-shadow 120ms',
		':hover': {
			transform: 'scale(1.06)',
		},
	},
	swatchActive: {
		borderColor: 'var(--ink)',
		boxShadow: '0 0 0 2px var(--bg-elev), 0 0 0 3px var(--ink)',
	},
	textarea: {
		width: '100%',
		padding: '10px 12px',
		border: '1px solid var(--border)',
		borderRadius: 8,
		background: 'var(--bg-elev)',
		color: 'var(--ink)',
		fontSize: 13,
		lineHeight: 1.5,
		fontFamily: 'var(--font-sans)',
		outline: 'none',
		resize: 'vertical',
		transition: 'border-color 120ms, box-shadow 120ms',
		':focus': {
			borderColor: 'var(--ink-faint)',
			boxShadow: '0 0 0 3px var(--ring)',
		},
	},
	triggerGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr 1fr',
		gap: 8,
	},
	triggerBtn: {
		textAlign: 'left',
		padding: '10px 12px',
		border: '1px solid var(--border)',
		background: 'var(--bg-elev)',
		borderRadius: 8,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
		transition: 'border-color 120ms, background 120ms',
		cursor: 'pointer',
		color: 'var(--ink)',
		':hover': {
			borderColor: 'var(--ink-faint)',
		},
	},
	triggerBtnActive: {
		borderColor: 'var(--ink)',
		background: 'var(--bg-sunken)',
	},
	triggerHead: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	triggerTitleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
	},
	triggerTitle: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink)',
		cursor: 'pointer',
	},
	triggerSub: {
		fontSize: 10.5,
		color: 'var(--ink-muted)',
	},
	footer: {
		padding: '14px 20px',
		borderTop: '1px solid var(--border)',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 12,
	},
	footerHint: {
		fontSize: 11,
		color: 'var(--ink-faint)',
		flex: 1,
		minWidth: 0,
	},
	footerKeyword: {
		fontFamily: 'var(--font-mono)',
		color: 'var(--ink-muted)',
	},
	footerActions: {
		display: 'flex',
		gap: 8,
		flexShrink: 0,
	},
});
